import { useCallback, useEffect, useState } from 'react';
import { useApiToken, useAuth } from '@/auth/hooks';
import { logger } from '@/lib/logger';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useOrgMemberStore } from '@/stores/useOrgMemberStore';
import { PluginPageHeader } from '@/app/components/PluginPageHeader';
import { useInterviewStore } from './useInterviewStore';
import { useStewardStore } from '../steward/useStewardStore';
import { SessionSidebar } from './SessionSidebar';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { ExtractedEntitiesPreview } from './ExtractedEntitiesPreview';
import { ReportDetailPanel } from '../steward/ReportDetailPanel';
import { SystemPromptPanel } from './SystemPromptPanel';
import { MobilePanelTabs, type MobileTab } from '@/app/components/MobilePanelTabs';
import { useIsMobile } from '@/hooks/use-mobile';
import {

  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Brain, Bot, CheckCircle2, Loader2, MessageSquare, Network } from 'lucide-react';

export function InterviewerPage() {
  const { getAccessToken } = useApiToken();
  const { user } = useAuth();
  const { activeWorkspace } = useWorkspaceStore();
  const { currentUser, loadCurrentUser } = useOrgMemberStore();

  const workspaceId = activeWorkspace?.id;
  const userId = currentUser?.userId ?? null;

  // Interview store
  const {
    sessions,
    sessionsLoading,
    activeSessionId,
    messages,
    isStreaming,
    isExtractingEntities,
    isCompleting,
    isRegenerating,
    streamingContent,
    allExtractedEntities,
    lastExtractedRelationships,
    sessionReports,
    viewMode,
    activeReportId,
    loadSessions,
    loadSession,
    loadSessionReports,
    startSession,
    sendMessageStream,
    completeSession,
    deleteSession,
    regenerateReport,
    setActiveSessionId,
    setViewMode,
    restoreCheckpoint,
    isRestoring,
  } = useInterviewStore();

  // Steward store (for report detail + actions)
  const {
    loadReportDetail,
    finalizeReport,
    markOutdated,
    clearActiveReport,
  } = useStewardStore();

  // New-session dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [creating, setCreating] = useState(false);

  // Finalize dialog
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [finalizeComment, setFinalizeComment] = useState('');
  const [processing, setProcessing] = useState(false);

  // System Prompt panel
  const [showPromptPanel, setShowPromptPanel] = useState(false);

  const reviewerName = user?.name ?? user?.username ?? 'Steward';

  // Hydrate sessions + reports on mount
  useEffect(() => {
    if (!workspaceId) return;
    void (async () => {
      const token = await getAccessToken().catch(() => null);
      // Skip initial load if token not yet available (MSAL still hydrating).
      // The effect will re-run once getAccessToken reference updates.
      if (!token) return;
      await Promise.all([
        loadSessions(workspaceId, token),
        loadSessionReports(workspaceId, token),
        loadCurrentUser(token),
      ]);
    })();
  }, [workspaceId, getAccessToken, loadSessions, loadSessionReports, loadCurrentUser]);

  // ── Handlers ────────────────────────────────────────────────

  const handleSelectSession = useCallback(
    async (sessionId: string) => {
      clearActiveReport();
      const token = await getAccessToken().catch(() => null);
      setActiveSessionId(sessionId);
      await loadSession(sessionId, token);
    },
    [getAccessToken, setActiveSessionId, loadSession, clearActiveReport],
  );

  const handleSelectReport = useCallback(
    async (sessionId: string, reportId: string) => {
      setActiveSessionId(sessionId);
      setViewMode('report', reportId);
      const token = await getAccessToken().catch(() => null);
      await loadReportDetail(reportId, token);
    },
    [getAccessToken, setActiveSessionId, setViewMode, loadReportDetail],
  );

  const handleNewSession = useCallback(() => {
    setNewTopic('');
    setShowNewDialog(true);
  }, []);

  const handleCreateSession = useCallback(async () => {
    if (!workspaceId || !userId) return;
    setCreating(true);
    try {
      const token = await getAccessToken().catch(() => null);
      const sessionId = await startSession(
        workspaceId,
        userId,
        undefined,
        newTopic || undefined,
        undefined,
        token,
      );
      setShowNewDialog(false);
      await loadSession(sessionId, token);
    } finally {
      setCreating(false);
    }
  }, [workspaceId, userId, newTopic, getAccessToken, startSession, loadSession]);

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      const token = await getAccessToken().catch(() => null);
      await deleteSession(sessionId, token);
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
      }
    },
    [getAccessToken, deleteSession, activeSessionId, setActiveSessionId],
  );

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!activeSessionId || !workspaceId) return;
      const token = await getAccessToken().catch(() => null);
      const session = sessions.find((s) => s.id === activeSessionId);
      await sendMessageStream(activeSessionId, workspaceId, content, session?.topic, token);
    },
    [activeSessionId, workspaceId, sessions, getAccessToken, sendMessageStream],
  );

  /** Stable token getter forwarded to ChatInput for realtime mode. */
  const getTokenForChat = useCallback(
    () => getAccessToken().catch(() => null),
    [getAccessToken],
  );

  const handleCompleteSession = useCallback(async () => {
    if (!activeSessionId || !workspaceId || !userId) return;
    const session = sessions.find((s) => s.id === activeSessionId);
    if (session?.status === 'Completed') return;
    try {
      const token = await getAccessToken().catch(() => null);
      const userName = user?.name ?? user?.username ?? 'anonymous';
      await completeSession(activeSessionId, workspaceId, userId, userName, token);
      // Refresh reports
      const token2 = await getAccessToken().catch(() => null);
      await loadSessionReports(workspaceId, token2);
    } catch (err) {
      logger.error('InterviewerPage', 'Failed to complete session', err);
      toast.error('Interview konnte nicht abgeschlossen werden. Bitte erneut versuchen.');
    }
  }, [activeSessionId, workspaceId, sessions, userId, user, getAccessToken, completeSession, loadSessionReports]);

  const handleRestoreCheckpoint = useCallback(async (messageId: string) => {
    if (!activeSessionId) return;
    const token = await getAccessToken().catch(() => null);
    await restoreCheckpoint(activeSessionId, messageId, token);
  }, [activeSessionId, getAccessToken, restoreCheckpoint]);

  const handleFinalize = useCallback(async () => {
    if (!activeReportId || !workspaceId || !userId) return;
    setProcessing(true);
    try {
      const token = await getAccessToken().catch(() => null);
      await finalizeReport(activeReportId, reviewerName, finalizeComment, token);
      // Refresh reports in sidebar
      const token2 = await getAccessToken().catch(() => null);
      await loadSessionReports(workspaceId, token2);
      setShowFinalizeDialog(false);
      setFinalizeComment('');
    } finally {
      setProcessing(false);
    }
  }, [activeReportId, workspaceId, reviewerName, finalizeComment, getAccessToken, finalizeReport, loadSessionReports]);

  const handleMarkOutdated = useCallback(async () => {
    if (!activeReportId || !workspaceId) return;
    const token = await getAccessToken().catch(() => null);
    await markOutdated(activeReportId, token);
    const token2 = await getAccessToken().catch(() => null);
    await loadSessionReports(workspaceId, token2);
  }, [activeReportId, workspaceId, getAccessToken, markOutdated, loadSessionReports]);

  const handleRegenerateReport = useCallback(
    async (reportId: string, sessionId: string) => {
      if (!workspaceId || !userId) return;
      try {
        const token = await getAccessToken().catch(() => null);
        const userName = user?.name ?? user?.username ?? 'anonymous';
        await regenerateReport(reportId, sessionId, workspaceId, userId, userName, token);
        // If this was the active report, reload detail
        if (activeReportId === reportId) {
          clearActiveReport();
          setViewMode('chat');
        }
      } catch (err) {
        logger.error('InterviewerPage', 'Failed to regenerate report', err);
      }
    },
    [workspaceId, userId, user, getAccessToken, regenerateReport, activeReportId, clearActiveReport, setViewMode],
  );

  // ── Derived ──────────────────────────────────────────────────

  const activeSession = sessions.find((s) => s.id === activeSessionId);
  const isCompleted = activeSession?.status === 'Completed';
  const showChat = activeSessionId && viewMode === 'chat';
  const showReport = activeSessionId && viewMode === 'report' && activeReportId;

  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<string>('chat');

  // ── Mobile layout ────────────────────────────────────────────

  if (isMobile) {
    const chatContent = (
      <div className="flex h-full flex-col bg-card/60 overflow-hidden">
        {showChat ? (
          <>
            <div className="flex items-center justify-between border-b border-border px-4 py-2 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate text-sm font-medium text-foreground">
                  {activeSession?.topic || 'Interview'}
                </span>
                {isCompleted && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-sopho-process/30 bg-sopho-process/10 px-2 py-0.5 text-[11px] font-medium text-sopho-process">
                    <CheckCircle2 className="h-3 w-3" />
                    Abgeschlossen
                  </span>
                )}
              </div>
              {!isCompleted && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-xs shrink-0"
                  onClick={handleCompleteSession}
                  disabled={isStreaming || isCompleting || messages.length < 3}
                >
                  {isCompleting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  )}
                  Abschließen
                </Button>
              )}
            </div>
            <ChatMessageList
              messages={messages}
              isStreaming={isStreaming}
              isExtractingEntities={isExtractingEntities}
              streamingContent={streamingContent}
              isRestoring={isRestoring}
              isCompleted={isCompleted}
              onRestoreCheckpoint={handleRestoreCheckpoint}
            />
            {!isCompleted && (
              <ChatInput
                key={activeSessionId ?? 'none'}
                onSend={handleSendMessage}
                disabled={isStreaming}
                isStreaming={isStreaming}
                placeholder="Deine Antwort eingeben…"
                sessionId={activeSessionId}
                workspaceId={workspaceId ?? null}
                topic={sessions.find((s) => s.id === activeSessionId)?.topic}
                templateId={sessions.find((s) => s.id === activeSessionId)?.templateId}
                getToken={getTokenForChat}
              />
            )}
          </>
        ) : showReport ? (
          <ReportDetailPanel
            onFinalize={() => setShowFinalizeDialog(true)}
            onMarkOutdated={handleMarkOutdated}
          />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
              <Brain className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Marvin</h2>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Wähle ein Interview aus der Session-Liste oder starte ein neues.
              </p>
            </div>
            <Button onClick={handleNewSession}>Neues Interview starten</Button>
          </div>
        )}
      </div>
    );

    const mobileTabs: MobileTab[] = [
      {
        id: 'sessions',
        label: 'Sessions',
        icon: <MessageSquare className="h-4 w-4" />,
        content: (
          <SessionSidebar
            sessions={sessions}
            activeSessionId={activeSessionId}
            activeReportId={activeReportId}
            viewMode={viewMode}
            sessionReports={sessionReports}
            loading={sessionsLoading}
            isRegenerating={isRegenerating}
            fullWidth
            onSelectSession={(id) => { handleSelectSession(id); setActiveTab('chat'); }}
            onSelectReport={(sid, rid) => { handleSelectReport(sid, rid); setActiveTab('chat'); }}
            onNewSession={() => { handleNewSession(); }}
            onDeleteSession={handleDeleteSession}
            onRegenerateReport={handleRegenerateReport}
          />
        ),
      },
      {
        id: 'chat',
        label: 'Chat',
        icon: <Bot className="h-4 w-4" />,
        content: chatContent,
      },
      {
        id: 'entities',
        label: 'Entitäten',
        icon: <Network className="h-4 w-4" />,
        content: (
          <div className="flex h-full flex-col overflow-hidden">
            <ExtractedEntitiesPreview
              entities={allExtractedEntities}
              relationships={lastExtractedRelationships}
            />
          </div>
        ),
      },
    ];

    return (
      <div className="flex h-full w-full min-w-0 flex-col gap-6 px-4 py-6 sm:px-6 xl:px-8 2xl:px-10">
        <PluginPageHeader
          group="analysis"
          title="Sopho Interviewer"
          icon={Brain}
        />
        <div className="sopho-ambient-bg min-h-0 flex-1 overflow-hidden rounded-2xl border border-border/60">
          <MobilePanelTabs tabs={mobileTabs} activeTab={activeTab} onChange={setActiveTab} />
        </div>
        {/* Dialogs are rendered outside the tab system */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader>
              <DialogTitle>Neues Interview starten</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label htmlFor="topic-mobile" className="text-sm font-medium">Thema (optional)</label>
                <Input
                  id="topic-mobile"
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  placeholder="z.B. Brandschutzverordnung, Arbeitssicherheit…"
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleCreateSession(); }}
                />
                <p className="text-xs text-muted-foreground">Das Thema hilft der KI, gezieltere Fragen zu stellen.</p>
                {!userId && (
                  <p className="text-xs text-amber-500">Bitte anmelden und Profil laden, bevor du ein Interview startest.</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowNewDialog(false)}>Abbrechen</Button>
              <Button onClick={handleCreateSession} disabled={creating || !userId}>
                {creating ? 'Wird erstellt…' : 'Interview starten'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <SystemPromptPanel
          open={showPromptPanel}
          onOpenChange={setShowPromptPanel}
          sessionId={activeSessionId ?? null}
          workspaceId={workspaceId ?? null}
          templateId={activeSession?.templateId}
          topic={activeSession?.topic}
          getToken={getTokenForChat}
        />
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-6 px-4 py-6 sm:px-6 xl:px-8 2xl:px-10">
      <PluginPageHeader
        group="analysis"
        title="Sopho Interviewer"
        icon={Brain}
      />
      <div className="sopho-ambient-bg flex min-h-0 flex-1 gap-2 overflow-hidden rounded-2xl border border-border/60 p-2">
        {/* Left: Session sidebar with report children */}
        <SessionSidebar
          sessions={sessions}
          activeSessionId={activeSessionId}
          activeReportId={activeReportId}
          viewMode={viewMode}
          sessionReports={sessionReports}
          loading={sessionsLoading}
          isRegenerating={isRegenerating}
          onSelectSession={handleSelectSession}
          onSelectReport={handleSelectReport}
          onNewSession={handleNewSession}
          onDeleteSession={handleDeleteSession}
          onRegenerateReport={handleRegenerateReport}
        />

        {/* Center: Chat or Report view */}
        <div className="flex flex-1 flex-col rounded-lg border border-border bg-card overflow-hidden">
        {showChat ? (
          <>
            {/* Chat header with complete button */}
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {activeSession?.topic || 'Interview'}
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    {activeSession?.createdAt
                      ? new Date(activeSession.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                      : ''}
                  </span>
                </span>
                {isCompleted && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-sopho-process/30 bg-sopho-process/10 px-2 py-0.5 text-[11px] font-medium text-sopho-process">
                    <CheckCircle2 className="h-3 w-3" />
                    Abgeschlossen
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-primary"
                  title="System-Prompt anzeigen"
                  aria-label="System-Prompt anzeigen"
                  onClick={() => setShowPromptPanel(true)}
                >
                  <Bot className="h-3.5 w-3.5" />
                </Button>
              </div>
              {!isCompleted && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-xs"
                  onClick={handleCompleteSession}
                  disabled={isStreaming || isCompleting || messages.length < 3}
                >
                  {isCompleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                      Report wird erstellt…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Interview abschließen
                    </>
                  )}
                </Button>
              )}
            </div>
            <ChatMessageList
              messages={messages}
              isStreaming={isStreaming}
              isExtractingEntities={isExtractingEntities}
              streamingContent={streamingContent}
              isRestoring={isRestoring}
              isCompleted={isCompleted}
              onRestoreCheckpoint={handleRestoreCheckpoint}
            />
            {!isCompleted && (
              <ChatInput
                key={activeSessionId ?? 'none'}
                onSend={handleSendMessage}
                disabled={isStreaming}
                isStreaming={isStreaming}
                placeholder="Deine Antwort eingeben…"
                sessionId={activeSessionId}
                workspaceId={workspaceId ?? null}
                topic={sessions.find((s) => s.id === activeSessionId)?.topic}
                templateId={sessions.find((s) => s.id === activeSessionId)?.templateId}
                getToken={getTokenForChat}
              />
            )}
          </>
        ) : showReport ? (
          <ReportDetailPanel
            onFinalize={() => setShowFinalizeDialog(true)}
            onMarkOutdated={handleMarkOutdated}
          />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
              <Brain className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Marvin
              </h2>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Erfasse Wissen über geführte Interviews. Marvin stellt
                gezielte Fragen und extrahiert automatisch Entitäten für deinen
                Knowledge Graph.
              </p>
            </div>
            <Button onClick={handleNewSession}>
              Neues Interview starten
            </Button>
          </div>
        )}
        </div>

        {/* Right: Extracted entities sidebar (only in chat mode for active sessions) */}
        {showChat && !isCompleted && (
          <ExtractedEntitiesPreview
            entities={allExtractedEntities}
            relationships={lastExtractedRelationships}
          />
        )}
      </div>

      {/* New Session Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Neues Interview starten</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="topic" className="text-sm font-medium">Thema (optional)</label>
              <Input
                id="topic"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                placeholder="z.B. Brandschutzverordnung, Arbeitssicherheit…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleCreateSession();
                }}
              />
              <p className="text-xs text-muted-foreground">
                Das Thema hilft der KI, gezieltere Fragen zu stellen.
              </p>
              {!userId && (
                <p className="text-xs text-amber-500">
                  Bitte anmelden und Profil laden, bevor du ein Interview startest.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateSession} disabled={creating || !userId}>
              {creating ? 'Wird erstellt…' : 'Interview starten'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finalize confirmation dialog */}
      <Dialog open={showFinalizeDialog} onOpenChange={setShowFinalizeDialog}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-sopho-process" />
              Report finalisieren
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Freigegebene Entitäten werden in den Knowledge Graph übernommen.
              Noch offene Entitäten werden automatisch abgelehnt.
            </p>
            <div className="grid gap-2">
              <label htmlFor="comment" className="text-sm font-medium">
                Kommentar (optional)
              </label>
              <Input
                id="comment"
                value={finalizeComment}
                onChange={(e) => setFinalizeComment(e.target.value)}
                placeholder="z.B. Geprüft und für korrekt befunden…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleFinalize();
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowFinalizeDialog(false)}
              disabled={processing}
            >
              Abbrechen
            </Button>
            <Button onClick={handleFinalize} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                  Wird finalisiert…
                </>
              ) : (
                'Finalisieren'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* System Prompt panel */}
      <SystemPromptPanel
        open={showPromptPanel}
        onOpenChange={setShowPromptPanel}
        sessionId={activeSessionId ?? null}
        workspaceId={workspaceId ?? null}
        templateId={activeSession?.templateId}
        topic={activeSession?.topic}
        getToken={getTokenForChat}
      />
    </div>
  );
}
